import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Breadcrumb, InputNumber, Input, Modal, Button } from "antd";
import { ApolloError } from "@apollo/client";

import { Properties, Orders_Insert_Input } from "graphql/generated/graphql";
import { useAddOrderMutation } from "graphql/mutations/add-order.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useProductsFiltersValue } from "state/reactive-vars/products-filters-var";
import { useCartStatus } from "state/reactive-vars/cart-status-var";

import LoadingOverlay from "components/loading-overlay/loading-overlay";
import routeNames from "constants/route-names";
import { OrderStatus } from "constants/field-values";

const { TextArea } = Input;

const Checkout: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();
	const { cartStatusValue, updateQuantity, removeFromCart, clearCartStatus } = useCartStatus();
	const { items, totalAmount } = cartStatusValue;
	const { updateProductsFilters } = useProductsFiltersValue();
	const navigate = useNavigate();

	const [orderNotes, setOrderNotes] = useState<string>("");
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [isOrderPlaced, setIsOrderPlaced] = useState(false);

	const [uniquePropertiesNameString, setUniquePropertiesNameString] = useState<string>("");

	useEffect(() => {
		if (cartStatusValue && cartStatusValue.items && cartStatusValue.items.length) {
			const uniqueProperties = cartStatusValue.items.reduce((accumulator: Properties[], currentValue) => {
				const { id } = currentValue.property;

				const alreadySaved = accumulator.find(element => element.id === id);

				if (!alreadySaved) {
					accumulator.push(currentValue.property);
				}

				return accumulator;
			}, []);

			if (uniqueProperties.length > 1) {
				const uniquePropertiesName = uniqueProperties.reduce((accumulator, currentValue, currentindex) => {
					const { name } = currentValue;
					if (currentindex === 0) {
						accumulator = `${accumulator} ${name}`;
					} else {
						accumulator = `${accumulator}, ${name}`;
					}

					return accumulator;
				}, "Sending to ");

				setUniquePropertiesNameString(uniquePropertiesName);
			} else {
				setUniquePropertiesNameString(`Sending to ${uniqueProperties[0].name}`);
			}
		}
	}, [cartStatusValue]);

	const handleOnCompleteAddOrderMutation = useCallback(
		(data: any) => {
			console.log("Complete Add Order data: ", data);
			clearCartStatus();
			setIsModalVisible(true);
		},
		[clearCartStatus]
	);

	const handleOnErrorAddOrderMutation = useCallback((error: ApolloError) => {
		console.log("useAddOrderMutation Error: ", error);
	}, []);

	const [addOrderMutation, { data, loading, error }] = useAddOrderMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorAddOrderMutation,
		onCompleted: handleOnCompleteAddOrderMutation
	});

	const handleCreateOrder = useCallback(() => {
		const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

		let orderInput: Orders_Insert_Input = {
			status: OrderStatus.pending,
			total_amount: totalAmount,
			user_id: loggedUserValue.id,
			workspace_id: loggedUserValue.workspace_id,
			organization_id: loggedUserValue.organization_id,
			base_url: baseUrl
		};

		if (orderNotes) {
			orderInput.notes = orderNotes;
		}

		if (items && items.length > 0) {
			const formatedItems = items.map(element => {
				const { product, property, quantity, unitPrice, totalPrice } = element;

				return {
					product_id: product.id,
					property_id: property.id,
					quantity,
					unit_price: unitPrice,
					total_price: totalPrice
				};
			});

			orderInput.items = {
				data: formatedItems
			};
		}

		setIsOrderPlaced(true);

		addOrderMutation({
			variables: {
				input: orderInput
			}
		});
	}, [items, orderNotes, totalAmount, loggedUserValue]);

	return (
		<div className="products-details-screen-container">
			<Breadcrumb
				className="products-breadcrumbs"
				items={[
					{
						title: "< BROWSE MORE PRODUCTS",
						onClick: () => {
							updateProductsFilters({
								selectedCategoriesFilter: []
							});
							navigate(routeNames.ORDER);
						}
					}
				]}
			/>
			<div className="checkout-content">
				<div className="product-d-row">
					<div className="checkout-left-container">
						<span className="checkout-title">{"My Cart"}</span>
						<span className="checkout-title">{uniquePropertiesNameString}</span>
						{cartStatusValue.items.map(element => {
							const { product, property, quantity, totalPrice } = element;
							const { name, photo_link = "" } = product || {};
							const { name: propertyName } = property || {};

							return (
								<div className="cart-item">
									<div
										className="cart-item-photo"
										style={
											photo_link
												? {
														backgroundImage: `url(${photo_link})`
													}
												: {}
										}
									></div>

									<div className="cart-item-info">
										<span className="cart-item-name">{name}</span>
										{loggedUserValue.organization?.display_price ? (
											<span className="cart-item-price">{totalPrice}</span>
										) : null}
										<span className="cart-item-property">{propertyName}</span>
									</div>

									<div className="cart-item-actions-container">
										<img
											alt={`remove-attachment-icon`}
											src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
											className="cart-remove-item"
											onClick={() => removeFromCart(product.id, property.id)}
										/>
										<InputNumber
											name="quantity"
											placeholder="Quantity"
											className="cart-item-input"
											value={quantity}
											onChange={value => {
												if (value) {
													updateQuantity(product.id, property.id, value);
												}
											}}
										/>
									</div>
								</div>
							);
						})}

						<Form.Item className="login-form-item">
							<TextArea
								name="order-notes"
								placeholder="Purchase Order (PO) Number"
								style={{ height: "137px", marginTop: "20px" }}
								value={orderNotes}
								autoComplete="off"
								onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
									setOrderNotes(event.target.value);
								}}
								allowClear
								disabled={loading}
							/>
						</Form.Item>
					</div>

					<div className="checkout-right-container">
						<span className="checkout-title">{`Order Summary`}</span>
						{loggedUserValue.organization?.display_price ? (
							<span className="order-subtotal">{`Sub total: $${cartStatusValue.totalAmount}`}</span>
						) : null}

						<p className="checkout-description">{`By clicking on ‘Order Now’ below, your order will be sent to Maestro for processing. They will contact you directly to confirm product availability, shipping information, and any applicable taxes.

`}</p>
						<button className="shop-product-d-button" onClick={handleCreateOrder} disabled={isOrderPlaced}>
							Submit Order Request
						</button>
					</div>
				</div>
			</div>

			<LoadingOverlay isLoading={loading} />
			<Modal
				className="modal"
				closable={true}
				styles={{
					body: {
						height: "calc(60vh - 40px)"
					}
				}}
				footer={null}
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
			>
				<p className="modal-text">
					Maestro has received your order and will follow up with a quote within two business days.
				</p>
				<div className="modal-button-container">
					<Button
						className="modal-button"
						key="submit"
						type="primary"
						loading={loading}
						onClick={() => navigate(routeNames.ORDER)}
					>
						Done
					</Button>
				</div>
			</Modal>
		</div>
	);
};

export default Checkout;
